import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui';
import { Wallet, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { WalletBalance as WalletBalanceType } from '../../services/wallet.service';

interface WalletBalanceProps {
  balance: WalletBalanceType;
  onCurrencyChange?: (currency: string) => void;
}

const CURRENCY_OPTIONS = [
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'THB', name: 'Thai Baht' },
];

export function WalletBalance({ balance, onCurrencyChange }: WalletBalanceProps) {
  const currencyOption = CURRENCY_OPTIONS.find(c => c.code === balance.currency);

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Wallet Balance
          </CardTitle>
          {onCurrencyChange && (
            <select
              value={balance.currency}
              onChange={(e) => onCurrencyChange(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {CURRENCY_OPTIONS.map(option => (
                <option key={option.code} value={option.code}>
                  {option.code}
                </option>
              ))}
            </select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Balance Display */}
          <div className="text-center py-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current Balance</p>
            <p className="text-4xl font-bold text-gray-900 dark:text-white">
              {balance.formattedBalance}
            </p>
            {balance.isBelowMinimum && !balance.isFrozen && (
              <div className="mt-3 flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" />
                <p className="text-sm font-medium">
                  Below minimum balance ({balance.formattedMinimumBalance})
                </p>
              </div>
            )}
          </div>

          {/* Balance Status */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-100 dark:border-blue-900">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                <TrendingDown className="h-4 w-4" />
                <span>Minimum</span>
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {balance.formattedMinimumBalance}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-100 dark:border-blue-900">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                <TrendingUp className="h-4 w-4" />
                <span>Status</span>
              </div>
              <p className="text-lg font-semibold">
                {balance.isFrozen ? (
                  <span className="text-red-600 dark:text-red-400">Frozen</span>
                ) : balance.isNewWallet ? (
                  <span className="text-amber-600 dark:text-amber-400">Inactive</span>
                ) : balance.isActive ? (
                  <span className="text-green-600 dark:text-green-400">Active</span>
                ) : (
                  <span className="text-gray-600 dark:text-gray-400">Inactive</span>
                )}
              </p>
            </div>
          </div>

          {/* Freeze Reason */}
          {balance.isFrozen && balance.freezeReason && (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-200">
                <strong>Freeze Reason:</strong> {balance.freezeReason}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
